import { CSSProperties, Fragment, useEffect, useState } from "react";
import {
    BrandContent,
    RecordDetails,
    RecordSelectorType,
    SpotifyAlbum,
    SpotifyPlaylist,
    SpotifyRecordSearchResponse,
    SpotifyTrack
} from 'modules/Types';
import Loader from 'elements/Loader';
import { searchSpotifyReleases, searchSpotifyReleasesAdBox } from "../api/platformApi";
import { clsx, isValidURL, makeKey, styx } from "modules/Utils";
import { Caption } from "components/shareable/Typography";
import debounce from "lodash.debounce"
import { getRecordDetails_api, searchSpotifyByText_api } from "api/brandContentApi"
import dayjs from "dayjs";
import useBrand from 'Hooks/useBrand';
import Colors from "modules/Colors";
// import SpotifyConnect from "components/connect/connectModals/Spotify";
import InputSymphony from 'elements/InputSymphony';
import TabsSymphony, { TabItem } from 'elements/TabsSymphony';
import { toastError } from "modules/Toast";
import ButtonSymphony from "elements/ButtonSymphony";
import useScreen from "Hooks/useScreen";
import Sty from "modules/Sty";
import CardSymphony from "elements/CardSymphony";
import React from "react";
import DebugBox from "components/shareable/DebugBox";
import useCampaignBuilder from "Hooks/useCampaignBuilder";
import { AdPlatform, PLAYBOOK_TYPE_INFO, PlaybookType, ADBOX_BRAND_ID } from "modules/Const";

/** Unified record type for all Spotify content */
interface UnifiedSpotifyRecord {
    name: string;
    thumbnailUrl: string;
    primaryArtist: string;
    type: RecordSelectorType;
    spotifyUrl?: string;
    explicit?: boolean;
    trackCount?: number;
    releaseDate?: string;
    parentName?: string;
    spotifyId: string;
    slug?: string;
}


const ExplicitIcon = () => (
    <p className="rounded-sm box-border ml-2 bg-gray-300 p-2 h-4 w-4 flex items-center justify-center">
        <span className="text-xs font-medium">E</span>
    </p>
)

/** Transform functions to convert various record types to unified format */
const recordTransformers = {
    /** Transform BrandContent to UnifiedSpotifyRecord(s) */
    fromBrandContent: (record: BrandContent, type: RecordSelectorType, adPlatform?: AdPlatform): UnifiedSpotifyRecord[] => {
        const metadata = record.content_metadata;
        const records: UnifiedSpotifyRecord[] = [];

        // First determine if this is a multi-track project
        const trackCount = metadata.tracks?.length || 0;
        const isSingleSongProject = metadata.spotify_release_type === 'single' && trackCount === 1;
        const isValidType = metadata.type === 'single' || metadata.type === 'project';

        const isGetPlayListed = adPlatform === AdPlatform.PLAYLISTS;

        // If it's a multi-track project, add individual tracks
        if (trackCount && isValidType && Array.isArray(metadata.tracks)) {
            metadata.tracks.forEach(track => {
                records.push({
                    name: track.name,
                    thumbnailUrl: metadata.thumbnail_url,
                    primaryArtist: metadata.primary_artist,
                    type: 'single',
                    releaseDate: metadata.release_date,
                    spotifyUrl: track.record_url,
                    explicit: track.explicit,
                    parentName: metadata.name, // Add parent album name
                    spotifyId: track.spotify_id
                });
            });

            // Also add the project itself if project has more than 1 track
            if (!isGetPlayListed && isSingleSongProject) {
                records.push({
                    name: metadata.name,
                    thumbnailUrl: metadata.thumbnail_url,
                    primaryArtist: metadata.primary_artist,
                    type: 'project',
                    releaseDate: metadata.release_date,
                    spotifyUrl: metadata.record_url,
                    explicit: 'explicit' in metadata ? metadata.explicit : undefined,
                    trackCount: trackCount,
                    spotifyId: metadata.spotify_id
                });
            }
        } else {
            // For single tracks, playlists, or single-track projects
            let recordType;
            if (metadata.type === 'playlist') {
                recordType = 'playlist';
            } else {
                recordType = 'single';
            }

            records.push({
                name: metadata.name,
                thumbnailUrl: metadata.thumbnail_url,
                primaryArtist: metadata.primary_artist,
                type: recordType as RecordSelectorType,
                releaseDate: metadata.release_date,
                spotifyUrl: metadata.record_url,
                explicit: 'explicit' in metadata ? metadata.explicit : undefined,
                trackCount: Array.isArray(metadata.tracks) ? metadata.tracks.length : undefined,
                spotifyId: metadata.spotify_id
            });
        }

        return records;
    },

    /** Transform SpotifyTrack to UnifiedSpotifyRecord */
    fromSpotifyTrack: (track: SpotifyTrack): UnifiedSpotifyRecord => {
        return {
            name: track.name,
            spotifyId: track.spotify_id,
            thumbnailUrl: track.thumbnail_url,
            primaryArtist: track.primary_artist,
            type: 'single',
            explicit: track.explicit,
            spotifyUrl: track.spotify_url,
            releaseDate: track.release_date,
            parentName: track.album_name || '',
        }
    },

    /** Transform SpotifyAlbum to UnifiedSpotifyRecord */
    fromSpotifyAlbum: (album: SpotifyAlbum): UnifiedSpotifyRecord => ({
        name: album.name,
        thumbnailUrl: album.thumbnail_url,
        primaryArtist: album.primary_artist,
        type: 'project',
        explicit: album.explicit,
        spotifyUrl: album.spotify_url,
        trackCount: album.track_count,
        releaseDate: album.release_date,
        spotifyId: album.spotify_id,

    }),

    /** Transform SpotifyPlaylist to UnifiedSpotifyRecord */
    fromSpotifyPlaylist: (playlist: SpotifyPlaylist): UnifiedSpotifyRecord => ({
        name: playlist.name,
        thumbnailUrl: playlist.thumbnail_url,
        primaryArtist: playlist.primary_artist,
        type: 'playlist',
        spotifyUrl: playlist.spotify_url,
        trackCount: playlist.track_count,
        explicit: playlist.explicit,
        spotifyId: playlist.spotify_id,
    })
};

/** Create debounced search outside component to prevent recreation */
const debouncedSearchFn = debounce((
    value: string,
    callback: (value: string) => Promise<void>
) => {
    callback(value)
}, 500)

/** Interface to store all record types */
interface RecordsByType {
    singles: UnifiedSpotifyRecord[];
    projects: UnifiedSpotifyRecord[];
    playlists: UnifiedSpotifyRecord[];
}

/** Helper function to calculate string similarity */
const calculateSimilarity = (str1: string, str2: string): number => {
    const a = str1.toLowerCase();
    const b = str2.toLowerCase();
    if (a === b) return 1;
    if (a.includes(b) || b.includes(a)) return 0.8;
    return 0;
}

/** Interface for component props */
interface RecordSearchViewProps {
    className?: string;
    cardTitle?: string | null;
    cardSubtitle?: string | null;
    parentLoading?: boolean;
    /** Whether to allow user to search Spotify */
    letUserSearchSpotify?: boolean;
    /** Whether to allow user to refresh releases */
    letUserRefreshReleases?: boolean;
    /** Available tab options */
    options?: RecordSelectorType[];
    /** Handler for record selection with URL and ID - maintained for backward compatibility */
    handleSelectRecord: (recordDetails: RecordDetails, inputValue?: string) => void;
    /** Handler for AdBox record selection - passes minimal data needed */
    handleAdBoxSelect?: (data: { url: string; name: string; thumbnailUrl: string; spotifyId: string }, inputValue?: string) => void;
    maxHeight?: number | string;
    /** Whether to use the AdBox unprotected endpoint for Spotify searches */
    isAdBox?: boolean;
}

/** 
   * Configures tabs and default selection based on allowed options
   * @param options - Array of allowed record selector types
   * @param defaultOption - Optional default tab to select
   * @returns Object containing allowed tabs array and default selected tab
   */
const getTabConfiguration = (options: RecordSelectorType[]): { allowedTabs: TabItem[], defaultSelectedTab: TabItem } => {
    const allowedTabs: TabItem[] = options.map(option => {
        let title = option.charAt(0).toUpperCase() + option.slice(1) + 's'
        return {
            id: option as RecordSelectorType,
            title: title
        }
    })

    let defaultSelectedTab: TabItem = {
        id: 'single',
        title: 'Singles'
    }

    if (options.length > 0) {
        const foundItem = allowedTabs.find(tab => tab.id === options[0])
        if (foundItem) {
            defaultSelectedTab = foundItem
        }
    }

    return {
        allowedTabs,
        defaultSelectedTab
    }
}

const transformReleases = (releases: BrandContent[]): BrandContent[] => {
    // Filter out releases that haven't been saved yet
    return releases.filter((release) => {
        if (release.content_metadata?.presave) {
            return Boolean(release.content_metadata.presave.saved_at)
        }
        return true
    })
}

// NOTE: component()
const RecordSearchView = ({
    className,
    cardTitle,
    cardSubtitle,
    letUserSearchSpotify,
    letUserRefreshReleases,
    options = ['single', 'project', 'playlist'],
    handleSelectRecord,
    handleAdBoxSelect,
    maxHeight,
    isAdBox
}: RecordSearchViewProps) => {
    const { mobileView, mStyle } = useScreen()
    const {
        currentBrand,
        refreshReleases,
        isLoadingReleases,
        isRefreshingReleases,
        recentReleases
    } = useBrand()

    const { playbookType } = useCampaignBuilder();
    const adPlatform = PLAYBOOK_TYPE_INFO[playbookType as PlaybookType]?.adPlatform;
    const { allowedTabs, defaultSelectedTab } = getTabConfiguration(options)

    const [isPullingBrandContent, setIsPullingBrandContent] = useState(false)
    const [selectedTab, setSelectedTab] = useState<TabItem>(defaultSelectedTab)
    const [inputValue, setInputValue] = useState<string>('')
    const [isSearching, setIsSearching] = useState(false)
    const [records, setRecords] = useState<RecordsByType>({
        singles: [],
        projects: [],
        playlists: []
    })
    const [currentlySelectedRecord, setCurrentlySelectedRecord] = useState<UnifiedSpotifyRecord | null>(null)
    const [searchError, setSearchError] = useState<string>('')

    /// ==== CONSTANTS ==== ///
    /** Whether user has connected their Spotify artist account */
    let onlyAllowsPlaylists = options.length === 1 && options[0] === 'playlist'
    const isLoadingOrSearchingReleases = isLoadingReleases || isSearching


    /// ==== EFFECTS ==== ///
    /** Effect to load initial records only once and when recentReleases changes */
    useEffect(() => {
        if (!inputValue && recentReleases) {
            const displayedReleases = recentReleases ? transformReleases(recentReleases) : []
            const currentTabId = selectedTab?.id as RecordSelectorType || 'single'
            const transformedRecords = transformRecordsForView({
                type: 'recent',
                brandContents: displayedReleases,
                tabId: currentTabId,
            })
            setRecords(transformedRecords)
        }
    }, [recentReleases, selectedTab, inputValue])

    useEffect(() => {
        if (!inputValue) {
            setIsSearching(false)
        }
    }, [inputValue])

    useEffect(() => {
        // If options are provided, set the default selected tab only on initial render
        if (options.length > 0 && !selectedTab) {
            const { defaultSelectedTab } = getTabConfiguration(options)
            setSelectedTab(defaultSelectedTab)
        }
    }, []) // Empty dependency array for initialization only

    // Add options dependency to the initializer effect, but use a ref to prevent infinite loop
    const isInitializedRef = React.useRef(false)
    useEffect(() => {
        // Only run this effect when options changes and after initial render
        if (isInitializedRef.current && options.length > 0) {
            // Update allowed tabs without changing selected tab if possible
            const { allowedTabs } = getTabConfiguration(options)
            // Only change selected tab if current one isn't in the new options
            const currentTabIsAllowed = allowedTabs.some(tab => tab.id === selectedTab?.id)
            if (!currentTabIsAllowed) {
                setSelectedTab(allowedTabs[0])
            }
        }
        isInitializedRef.current = true
    }, [options])


    /** 
     * Transforms records into UnifiedSpotifyRecord format and organizes by type
     * @param records - Array of records from any source
     * @param type - Type of records being transformed
     */
    const transformRecordsForView = ({
        brandContents,
        searchResultsFromApi,
        type,
        tabId,
    }: {
        brandContents?: Array<BrandContent>;
        searchResultsFromApi?: SpotifyRecordSearchResponse;
        type: 'recent' | 'url' | 'search';
        tabId: RecordSelectorType;
    }): RecordsByType => {
        // this should filter out non-presaved presave releases 

        if (type === 'search' && searchResultsFromApi) {
            return {
                singles: (searchResultsFromApi.tracks || []).map(track => recordTransformers.fromSpotifyTrack(track)),
                projects: (searchResultsFromApi.albums || [])
                    .map(album => recordTransformers.fromSpotifyAlbum(album)),
                playlists: (searchResultsFromApi.playlists || [])
                    .map(playlist => recordTransformers.fromSpotifyPlaylist(playlist)),
            };
        }

        // Handle recent or URL results
        const transformed = brandContents?.flatMap(record =>
            recordTransformers.fromBrandContent(record, tabId, adPlatform)
        ) || [];

        return {
            singles: transformed
                .filter(r => r.type === 'single')
                .sort((a, b) => new Date(b.releaseDate || '').getTime() - new Date(a.releaseDate || '').getTime()),
            projects: transformed
                .filter(r => r.type === 'project')
                .sort((a, b) => new Date(b.releaseDate || '').getTime() - new Date(a.releaseDate || '').getTime()),
            playlists: transformed
                .filter(r => r.type === 'playlist')
                .sort((a, b) => new Date(b.releaseDate || '').getTime() - new Date(a.releaseDate || '').getTime())
        };
    }

    const handleRecordSearch = async (value: string) => {
        if (!value) {
            const currentTabId = selectedTab?.id as RecordSelectorType || 'single'
            const transformedRecords = transformRecordsForView({
                type: 'recent',
                brandContents: recentReleases || [],
                tabId: currentTabId,
            })
            setRecords(transformedRecords)
            return
        }

        setIsSearching(true)
        setSearchError('')

        try {
            if (isValidURL(value)) {
                let brandContentResponse: BrandContent;
                
                if (isAdBox) {
                    const adBoxResponse = await searchSpotifyReleasesAdBox(value);
                    // Wrap AdBox response in BrandContent structure
                    brandContentResponse = {
                        id: 0,
                        brand_id: 0,
                        slug: '',
                        createdAt: '',
                        updatedAt: '',
                        name: '',
                        type: '',
                        url: '',
                        content_metadata: adBoxResponse as any
                    };
                } else {
                    const response = await searchSpotifyReleases(value);
                    if (!response?.content_metadata) {
                        throw new Error('Invalid response');
                    }
                    brandContentResponse = response;
                }

                const currentTabId = selectedTab?.id as RecordSelectorType || 'single'
                let foundRelease = transformRecordsForView({
                    type: 'url',
                    brandContents: [brandContentResponse],
                    tabId: currentTabId
                })
                setRecords(foundRelease)

                // Find first tab with non-empty records
                const recordTypes: Record<RecordSelectorType, UnifiedSpotifyRecord[]> = {
                    single: foundRelease.singles,
                    project: foundRelease.projects,
                    playlist: foundRelease.playlists
                }

                const firstNonEmptyType = Object.entries(recordTypes)
                    .find(([_, records]) => records.length > 0)?.[0] as RecordSelectorType

                if (firstNonEmptyType) {
                    const foundTab = allowedTabs.find(tab => tab.id === firstNonEmptyType)
                    if (foundTab) {
                        setSelectedTab(foundTab)
                    }
                }
            } else {
                const response = await searchSpotifyByText_api(value, 5)
                if (!response) {
                    throw new Error('Invalid response')
                }
                const currentTabId = selectedTab?.id as RecordSelectorType || 'single'
                setRecords(transformRecordsForView({
                    type: 'search',
                    searchResultsFromApi: response,
                    tabId: currentTabId
                }))
            }
        } catch (error) {
            setSearchError('Error searching')
        } finally {
            setIsSearching(false)
        }
    }

    const handleChangeInput = (valueText: string) => {
        setInputValue(valueText)

        // Only reset records if clearing the input
        if (!valueText) {
            const currentTabId = selectedTab?.id as RecordSelectorType || 'single'
            const transformedRecords = transformRecordsForView({
                type: 'recent',
                brandContents: recentReleases || [],
                tabId: currentTabId
            })
            setRecords(transformedRecords)
        }
    }


    const renderSearchField = () => {
        let placeholderText = `Search for your ${selectedTab}`
        if (onlyAllowsPlaylists) {
            placeholderText = "Search or paste your playlist's URL"
        } else if (letUserSearchSpotify) {
            placeholderText = mobileView ? "Search spotify" : "Search for your song, album, or playlist"
        }

        return (
            <InputSymphony
                onEnter={() => handleRecordSearch(inputValue)}
                isLoading={isSearching}
                className="sharedMusicSelectorSearchTextField"
                value={inputValue}
                onChange={(valueText) => handleChangeInput(valueText)}
                placeholder={placeholderText}
                isSearch
                onSearch={() => handleRecordSearch(inputValue)}
                style={sty.input}
            />
        )
    }

    const handleRecordCardRowSelect = async (record: UnifiedSpotifyRecord, inputValue?: string) => {
        try {
            setCurrentlySelectedRecord(record)
            
            // For AdBox, use the dedicated handler with minimal data
            if (isAdBox && handleAdBoxSelect) {
                handleAdBoxSelect({
                    url: record.spotifyUrl || '',
                    name: record.name,
                    thumbnailUrl: record.thumbnailUrl,
                    spotifyId: record.spotifyId
                }, inputValue);
                return;
            }
            
            // For authenticated users, continue with the API call
            setIsPullingBrandContent(true)
            // Use ADBOX_BRAND_ID as fallback when currentBrand is not available (logged out users)
            const brandId = currentBrand?.id || ADBOX_BRAND_ID;
            const recordDetail = await getRecordDetails_api(record.spotifyId, selectedTab.id as RecordSelectorType, brandId);

            if (recordDetail && typeof recordDetail !== 'undefined') {
                if (selectedTab.id === 'playlist' && inputValue) {
                    try {
                        const url = new URL(inputValue);
                        const prid = url.searchParams.get('prid');

                        if (prid && prid.startsWith('spotify:track:')) {
                            const trackId = prid.split(':')[2];
                            recordDetail.prid = trackId;
                        }
                    } catch (e) {
                        console.error('Error parsing URL', e);
                    }
                }

                handleSelectRecord(recordDetail, inputValue);
            }
        } catch (error) {
            console.error('Error getting record details:', error);
            toastError(`There was an error pulling details for our ${selectedTab}. Please try again.`)
        } finally {
            setIsPullingBrandContent(false)
        }
    };

    const renderRecords = () => {
        let primaryContent = null;

        if (!records || isLoadingOrSearchingReleases || isRefreshingReleases) {
            return (
                <div style={sty.loadingContainer}>
                    <Loader
                        className="loadingTextContainerLoader"
                        title="Gathering records..."
                    />
                </div>
            )
        }

        // Get the correct records based on selected tab with type safety
        const recordsMap: Record<RecordSelectorType, UnifiedSpotifyRecord[]> = {
            single: records.singles,
            project: records.projects,
            playlist: records.playlists,
        }

        const recordsToRender = recordsMap[selectedTab.id as RecordSelectorType] || []

        if (!recordsToRender.length) {
            if (inputValue) {
                return (
                    <div style={sty.loadingContainer}>
                        No {`${selectedTab.title}`} found for "{inputValue}".
                    </div>
                )
            }
            return (
                <div style={sty.loadingContainer}>
                    Search for any Spotify release or URL.
                </div>
            )
        }

        primaryContent = (
            <>
                {recordsToRender.map((record: UnifiedSpotifyRecord, index: number) => (
                    <Fragment key={makeKey('record', record.spotifyId, index)}>
                        {renderRecordRow({ record })}
                    </Fragment>
                ))}
                {recordsToRender.length === 0 && selectedTab.id === 'playlist' &&
                    <Caption color={Colors.greyDark}>
                        Paste a Spotify playlist URL to automatically find it.
                    </Caption>
                }
            </>
        )

        return (
            <div
                className="sharedMusicSelectorPrimaryContent"
                style={styx(
                    mStyle(sty, 'sharedMusicSelectorPrimaryContent'),
                    { maxHeight }
                )}
            >
                {primaryContent}
            </div>
        )
    }

    /** Record card component for consistent rendering */
    const renderRecordRow = ({ record }: { record: UnifiedSpotifyRecord }) => {
        let captions = [];
        let isSelectedRecord = false
        switch (record.type) {
            case 'single':
                if (record.parentName) {
                    captions.push(`from album "${record.parentName}"`)
                }
                if (record.primaryArtist) {
                    let text = `${record.primaryArtist}`
                    if (record.releaseDate) {
                        text += ` • Released ${dayjs(record.releaseDate).format('MMMM DD, YYYY')}`
                    }
                    captions.push(text)
                }

                break;
            case 'project':
                if (record.primaryArtist) {
                    let text = `${record.primaryArtist}`
                    if (record.releaseDate) {
                        text += ` • Released ${dayjs(record.releaseDate).format('MMMM DD, YYYY')}`
                    }
                    captions.push(text)
                }
                if (record.trackCount) {
                    captions.push(`${record.trackCount} Song${record.trackCount === 1 ? '' : 's'}`)
                }
                break;
            case 'playlist':
                if (record.primaryArtist) {
                    captions.push(`Creator: ${record.primaryArtist}`)
                }
                if (record.trackCount) {
                    captions.push(`${record.trackCount} Song${record.trackCount === 1 ? '' : 's'}`)
                }
                break;
            default:
                captions = []
        }

        return (
            <div
                style={mStyle(sty, 'sharedMusicSelectorCard')}
                className={'sharedMusicSelectorCard'}
            >
                <div
                    className="cardSubContainer"
                    style={mStyle(sty, 'cardSubContainer')}
                >
                    <div className="cardProjectImageContainer" style={sty.projectImage}>
                        <img
                            className="cardProjectImage"
                            src={record.thumbnailUrl}
                            alt={record.name}
                        />
                    </div>
                    <div className="cardInfoContainer" style={sty.cardInfo}>
                        <div className="cardProjectName" style={sty.projectName}>
                            {record.name} {record.explicit && <ExplicitIcon />}
                        </div>
                        {captions.map((caption, index) => (
                            <div
                                key={makeKey('captionRecordSearch', index)}
                                className="cardFromName"
                                style={{ ...sty.fromName, ...Sty.text12grey }}
                            >
                                {caption}
                            </div>
                        ))}
                    </div>
                </div>
                <ButtonSymphony
                    className="cardSelectButton"
                    style={sty.cardSelectButton}
                    onClick={() => handleRecordCardRowSelect(record)}
                    disabled={isPullingBrandContent}
                    isLoading={isPullingBrandContent && currentlySelectedRecord?.spotifyId === record.spotifyId}
                    variant="text"
                >
                    {mobileView ? '→' : 'Select'}
                </ButtonSymphony>
            </div>
        )
    };


    const renderRefreshReleases = () => {
        return (
            <div
                className="refreshReleasesInner"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: 16,
                    paddingBottom: 16,
                }}
            >
                <div className="recentReleasesSharedMusicSelectorView">
                    Recent Releases
                </div>
                <ButtonSymphony
                    className="refreshReleasesButton"
                    onClick={refreshReleases}
                    variant="text"
                    disabled={isRefreshingReleases}
                    isLoading={isRefreshingReleases}
                    loadingText="Refreshing..."
                    iconLeft="reload"
                >
                    Refresh
                </ButtonSymphony>
            </div>
        )
    }

    const renderCardHeader = () => {
        if (!cardTitle && !cardSubtitle) {
            return null
        }
        return (
            <CardSymphony
                className="recordSelectorTabHeader1"
                title={cardTitle ?? "Select the release you want to promote."}
                subtitle={cardSubtitle ?? "Search for your song, album, or playlist"}
                isNoPadding
                size="small"
            />
        )
    }

    // NOTE: render()
    return (
        <>
            {renderCardHeader()}
            {/* {letUserRefreshReleases && !onlyAllowsPlaylists && renderRefreshReleases()} */}
            {letUserSearchSpotify &&
                <div
                    className={clsx("sharedMusicSelectorSearchFieldContainer", className)}
                    style={mStyle(sty, 'sharedMusicSelectorSearchFieldContainer')}
                >
                    {renderSearchField()}
                </div>
            }
            <TabsSymphony
                className="sharedMusicSelectorTabs"
                style={sty.tabsBackground}
                tabs={allowedTabs}
                selectedTab={selectedTab}
                onChange={(tab) => {
                    const tabItem = tab
                    setSelectedTab(tabItem as TabItem)
                }}
            />

            {/* <DebugBox
                alwaysShow
                data={{
                    recentReleases
                }}
            /> */}

            {renderRecords()}
        </>
    );
}

const sty: Record<string, CSSProperties> = {
    cardSelectButton: {
        marginLeft: 'auto'
    },
    sharedMusicSelectorSearchFieldContainer: {

    },
    loadingContainer: {
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 16,
        paddingBottom: 16,
        margin: 'auto',
        textAlign: 'center',
        width: '100%'
    },
    input: {
        width: '100%',
        height: '100%',
        fontFamily: 'DIN',
        textAlign: 'left',
        borderRadius: 6,
        backgroundColor: 'white'
    },
    sharedMusicSelectorCard: {
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: 16,
        paddingRight: 16,
        gap: 16,
        borderBottom: '1px solid #EDECF2',
        order: 1,
        flexGrow: 0,
        width: '100%',
    },
    sharedMusicSelectorCard_mobile: {
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: 8,
        paddingRight: 8,
    },
    cardSubContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        padding: 0,
        gap: 16,
        flex: 'none',
        order: 0,
        flexShrink: 1
    },
    cardSubContainer_mobile: {
        gap: 12
    },
    cardInfo: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: 0,
        gap: 4,
        flex: 1,
        order: 1,
        width: '100%',
        minWidth: 0
    },
    projectName: {
        fontSize: 18,
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        lineHeight: '22px'
    },
    fromName: {
        display: 'flex',
        alignItems: 'center',
        color: '#707070',
        flex: 'none',
        order: 1,
        width: '100%',
        wordBreak: 'break-word',
        whiteSpace: 'normal'
    },
    sharedMusicSelectorPrimaryContent: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflowY: 'auto',
    },
    projectImage: {
        width: 60,
        height: 60,
        borderRadius: 8,
        overflow: 'hidden'
    }
}

export default RecordSearchView